import { useEffect, useState } from 'react';

import { AdminRoles } from 'constants/permissions';
import { useTracking } from 'hooks/analytic-tracking';
import { useLocalStorage } from 'hooks/use-local-storage';
import { usePolling } from 'hooks/use-polling-service';
import i18n from 'i18n';
import moment from 'moment';
import ReactGA from 'react-ga4';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import Authorization from 'views/Authorization/Authorization';
import Renewals from 'views/Renewals/Renewals';
import SignIn from 'views/SignIn/SignIn';
import SmartRecruitersAuthorization from 'views/SmartRecruitersAuthorization/SmartRecruitersAuthorization';

import { Loading } from './components/Loading/Loading';
import { FormThanksCard } from 'components/FormThanksCard/FormThanksCard';
import { CustomToast } from 'components/Toast/CustomToast';

import { useGetSupportUsersStatsQuery } from 'api/admin-roles-api';
import { authenticate, selectAuthentication } from 'api/authentication-slice';
import { selectCompany } from 'api/company-slice';
import { selectUser } from 'api/user-slice';

import Account from './views/Account/Account';
import AccountValidation from './views/AccountValidation/AccountValidation';
import AdminAdminUsers from './views/AdminApp/AdminAdminUsers/AdminAdminUsers';
import AdminCandidate from './views/AdminApp/AdminCandidate/AdminCandidate';
import AdminCandidateChecks from './views/AdminApp/AdminCandidateChecks/AdminCandidateChecks';
import AdminCandidates from './views/AdminApp/AdminCandidates/AdminCandidates';
import AdminCheckTypes from './views/AdminApp/AdminCheckTypes/AdminCheckTypes';
import AdminCompanies from './views/AdminApp/AdminCompanies/AdminCompanies';
import AdminCompany from './views/AdminApp/AdminCompany/AdminCompany';
import AdminLogs from './views/AdminApp/AdminLogs/AdminLogs';
import AdminReporting from './views/AdminApp/AdminReporting/AdminReporting';
import AdminSupportDashboard from './views/AdminApp/AdminSupportDashboard/AdminSupportDashboard';
import AdminUsers from './views/AdminApp/AdminUsers/AdminUsers';
import BulkImport from './views/BulkImport/BulkImport';
import {
  //TODO: Remove this once ACIC is fully up and running and all clients switched over
  CandidateBackyPortal,
  CandidateCustomReference,
  CandidateDashboard,
  CandidateIntroduction,
  CandidateQuestionnaire,
  CandidateReference,
  CandidateSectionLayout,
  CandidateSignDocument,
  CandidateSuccess,
  SectionIndexPage,
} from './views/CandidateApp';
import CandidateDocumentRequest from './views/CandidateDocumentRequest/CandidateDocumentRequest';
import Check from './views/Check/Check';
import Company from './views/Company/Company';
import { CriminalCheckConsent } from './views/CriminalCheckConsent/CriminalCheckConsent';
import Dashboard from './views/Dashboard/Dashboard';
import { DeletionPolicy } from './views/DeletionPolicy/DeletionPolicy';
import { Document } from './views/Document/Document';
import ForgotPassword from './views/ForgotPassword/ForgotPassword';
import NewCheck from './views/NewCheck/NewCheck';
import AssessmentChecks from './views/Partners/v1/checks/AssessmentChecks';
import { AssessmentMessage } from './views/Partners/v1/messages/AssessmentMessage';
import AssessmentResults from './views/Partners/v1/results/AssessmentResults';
import { AssessmentDetails, AssessmentLauncher, AssessmentProgress } from './views/Partners/v2';
import { PublicReferenceReport } from './views/PublicReferenceReport/PublicReferenceReport';
import Questionnaires from './views/Questionnaires/Questionnaires';
import OldRefereeCompletion from './views/RefereeCompletion/OldRefereeCompletion';
import RefereeCompletion from './views/RefereeCompletion/RefereeCompletion';
import NewRefereeFormSubmission from './views/RefereeFormSubmission/NewRefereeFormSubmission';
import RefereeFormSubmission from './views/RefereeFormSubmission/RefereeFormSubmission';
import RefereeRequest from './views/RefereeRequest/RefereeRequest';
import Reporting from './views/Reporting/Reporting';
import ResetPassword from './views/ResetPassword/ResetPassword';
// import SignUp from './views/SignUp/SignUp';
import SingleSignOn from './views/SingleSignOn/SingleSignOn';
import SingleSignOnDomain from './views/SingleSignOnDomain/SingleSignOnDomain';
import Users from './views/Users/Users';
import ValidateUser from './views/ValidateUser/ValidateUser';
import ViewReport from './views/ViewReport/ViewReport';
import Error400 from './views/errors/Error400';
import Error401 from './views/errors/Error401';
import Error404 from './views/errors/Error404';
import Error500 from './views/errors/Error500';

const App = () => {
  const dispatch = useDispatch();

  const authentication = useSelector(selectAuthentication);
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);

  const { pathname } = useLocation();

  const [loading, setLoading] = useState(true);

  const { refetch } = useGetSupportUsersStatsQuery();
  const [submittedChecks, setSubmittedChecks] = useLocalStorage('support_user_total_checks', 0);

  useEffect(() => {
    if (company?.locale) {
      i18n.changeLanguage(company?.locale);
    }
  }, [company?.locale]);

  usePolling(
    async () => {
      if (authentication.admin_role !== AdminRoles.Support) return;

      const result = await refetch();

      if (result?.data?.result?.total_checks > submittedChecks) {
        toast.custom(t => (
          <CustomToast
            t={t}
            title="New checks"
            message={`${result?.data?.result?.total_checks - submittedChecks} new check(s) assigned to you. Please go to the dashboard to see them.`}
            type="info"
            linkTo="/admin/dashboard"
            linkText="Go to dashboard"
          />
        ));
      }

      setSubmittedChecks(result?.data?.result?.total_checks);
    },
    1800000,
    5,
    60000,
  );

  useEffect(() => {
    if (pathname === '/checkmate_error') setLoading(false);
    else dispatch(authenticate()).finally(() => setLoading(false));

    ReactGA.initialize('UA-123809112-1');
  }, [pathname]);

  useEffect(() => {
    if (process.env.REACT_APP_ENV !== 'prod') {
      return;
    }

    window.Intercom('shutdown');

    if (!user?.id || !company?.id || user.company_id !== company.id) {
      return;
    }

    window.Intercom('boot', {
      app_id: 'yh3o8vqn',
      name: user.name,
      email: user.email,
      user_id: `client-${user.id}-${process.env.REACT_APP_ENV}`,
      user_type: 'client',
      is_creator: company.creator_id === user.id,
      created_at: moment(user.created_at, 'DD/MM/YYYY').unix(),
      company: {
        company_id: `company-${company.id}-${process.env.REACT_APP_ENV}`,
        name: company.name,
      },
    });
  }, [user?.id, company?.id]);

  if (loading) return <Loading active={true} />;

  return (
    <>
      <Toaster />
      <Routes>
        <Route path="/" element={authentication.user_id ? <Dashboard /> : <SignIn />} />

        <Route path="/sign_in" element={<SignIn />} />

        {/* Hide SignUp page
          <Route path="sign_up">
            <Route index element={<SignUp />} />
            <Route path="tas/:snaphire_tenant_short_code" element={<SignUp />} />
          </Route> 
        */}

        <Route path="sso">
          <Route index element={<SingleSignOn />} />
          <Route path=":domain" element={<SingleSignOnDomain />} />
        </Route>

        <Route path="/authorization" element={<Authorization />} />
        <Route path="/forgot_password" element={<ForgotPassword />} />
        <Route path="/reset_password/:token" element={<ResetPassword />} />
        <Route path="/smartrecruiters" element={<SmartRecruitersAuthorization />} />
        <Route path="/validation/:token" element={<ValidateUser />} />
        <Route path="/invitation/:token" element={<ResetPassword invitation={true} />} />
        <Route path="dashboard">
          <Route index element={<Dashboard />} />
          <Route path=":validate" element={<Dashboard />} />
        </Route>

        <Route path="account_validation">
          <Route path=":target" element={<AccountValidation />} />
        </Route>

        <Route path="new_check">
          <Route index element={<NewCheck />} />
          <Route path=":target" element={<NewCheck />} />
        </Route>

        <Route path="documents">
          <Route path=":id" element={<Document />} />
          <Route path=":id/:model/:model_id" element={<Document />} />
        </Route>

        <Route path="bulk_import">
          <Route index element={<BulkImport />} />
          <Route path=":type" element={<BulkImport />} />
        </Route>

        <Route path="check">
          <Route path=":id" element={<Check />} />
          <Route path=":id/:tab" element={<Check />} />
          <Route path=":id/:tab/:target" element={<Check />} />
        </Route>

        <Route path="questionnaires">
          <Route index element={<Questionnaires />} />
          <Route path=":tab" element={<Questionnaires />} />
          <Route path=":tab/:id" element={<Questionnaires />} />
        </Route>

        <Route path="company">
          <Route index element={<Company />} />
          <Route path=":tab" element={<Company />} />
        </Route>

        <Route path="account">
          <Route index element={<Account />} />
          <Route path=":tab" element={<Account />} />
        </Route>

        <Route path="/company/report/:type/:token" element={<ViewReport />} />
        <Route path="/company/report/reference/:token" element={<PublicReferenceReport />} />
        <Route path="/reporting" element={<Reporting />} />
        <Route path="/users" element={<Users />} />
        <Route path="/renewals" element={<Renewals />} />

        <Route path="form_submission">
          <Route path="candidate">
            <Route path=":token" element={<CandidateIntroduction />} />
            <Route path="dashboard/:token" element={<CandidateDashboard />} />
            <Route path="success/:token" element={<CandidateSuccess />} />
            <Route path=":sectionKey/:token">
              <Route index element={<SectionIndexPage />} />
              <Route path="page/:pageIndex" element={<CandidateSectionLayout />} />
            </Route>
            <Route path="reference/:token" element={<CandidateReference />} />
            <Route path="reference/:token/:replacement" element={<CandidateReference />} />
            <Route
              path="custom_reference/:token/:replacement"
              element={<CandidateCustomReference />}
            />
            <Route path="custom_reference/:token" element={<CandidateCustomReference />} />
            <Route path="questionnaire/:token" element={<CandidateQuestionnaire />} />
            <Route path="document_signing/:token" element={<CandidateSignDocument />} />
            <Route path="success/:token" element={<CandidateSuccess />} />
            {/* TODO: Remove this once the clients switched over to ACIC */}
            <Route path="backy_portal/:token" element={<CandidateBackyPortal />} />
          </Route>

          <Route path="referee/:token">
            <Route index element={<RefereeFormSubmission />} />
            <Route path=":answered_on_behalf_of" element={<RefereeFormSubmission />} />
            <Route path=":referee" element={<RefereeFormSubmission />} />
            <Route path=":referee/:candidate" element={<RefereeFormSubmission />} />
          </Route>

          <Route path="new_referee/:token">
            <Route index element={<NewRefereeFormSubmission />} />
            <Route path=":answered_on_behalf_of" element={<NewRefereeFormSubmission />} />
            <Route path=":referee" element={<NewRefereeFormSubmission />} />
            <Route path=":referee/:candidate" element={<NewRefereeFormSubmission />} />
          </Route>
        </Route>

        <Route path="/referee_completion/:token" element={<RefereeCompletion />} />
        <Route path="/old_referee_completion/:token" element={<OldRefereeCompletion />} />
        <Route path="/referee_request/:token" element={<RefereeRequest />} />

        <Route path="assessments">
          {/* Partners V2 */}
          <Route path="launcher/:partner/:token" element={<AssessmentLauncher />} />
          <Route path="details/:partner/:token" element={<AssessmentDetails />} />
          <Route path="progress/:partner/:token" element={<AssessmentProgress />} />

          {/* Partners V1 */}
          <Route path=":token">
            <Route path="checks/:check_type" element={<AssessmentChecks />} />
            <Route path="results/:check_type" element={<AssessmentResults />} />
            <Route path="results" element={<AssessmentResults />} />
          </Route>

          <Route path="messages/:severity" element={<AssessmentMessage />} />
        </Route>

        <Route path="admin">
          <Route index element={<AdminCandidates />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="dashboard" element={<AdminSupportDashboard />} />
          <Route path="check_types" element={<AdminCheckTypes />} />
          <Route path="companies/:company_id" element={<AdminCompany />} />
          <Route path="companies" element={<AdminCompanies />} />
          <Route path="candidates/:candidate_id" element={<AdminCandidate />} />
          <Route path="candidates" element={<AdminCandidates />} />
          <Route path="checks" element={<AdminCandidateChecks />} />
          <Route path="log" element={<AdminLogs />} />
          <Route path="reporting" element={<AdminReporting />} />
          <Route path="admin_users" element={<AdminAdminUsers />} />
        </Route>

        <Route path="/criminal_check_consent" element={<CriminalCheckConsent />} />
        <Route path="/deletion_policy" element={<DeletionPolicy />} />
        <Route path="/document-upload" element={<CandidateDocumentRequest />} />
        <Route path="/thanks" element={<FormThanksCard candidate />} />
        {/* Legacy route for backwards compatibility with old instruction URLs */}
        <Route path="/instructions/:fieldCode" element={<CandidateDocumentRequest />} />
        <Route path="/not_found_error" element={<Error404 />} />
        <Route path="/bad_request" element={<Error400 />} />
        <Route path="/checkmate_error" element={<Error500 />} />
        <Route path="/unauthorized" element={<Error401 />} />
        <Route path="*" element={<Error404 />} />
      </Routes>
    </>
  );
};

export default App;
